import { useMemo } from 'react';

import { TOKEN_SCOPE, useServiceAppState, useUserInfoState } from '@kc-console/context';

import type { ResponseCloudRole, TokenScope } from '@kc-console/context';
import type { RequiredBy } from '@kc-console/core';

const ProjectScopeAdminRoles = ['admin'];

const DomainScopeAdminRoles = ['admin'];

type UseCheckAdminRequest = {
  adminRoles?: {
    [TOKEN_SCOPE.DOMAIN]?: string[];
    [TOKEN_SCOPE.PROJECT]?: string[];
  };
};

type UseCheckAdminResponse = {
  isAdmin: boolean;
  roles: string[];
  scope: TokenScope;
};

/**
 * Admin 권한을 확인할 수 있는 훅입니다.
 * @description
 * - userInfo의 roles에서 admin 권한을 가지고 있는지 확인합니다.
 * - 현재 scope(DOMAIN 또는 PROJECT)에 따라 다른 admin roles를 적용합니다.
 * - 기본적으로 DOMAIN과 PROJECT scope 모두 ['admin'] role을 admin으로 판단합니다.
 * - request를 통해 scope별 커스텀 adminRoles를 제공할 수 있습니다.
 * - userInfo가 없을 경우 isAdmin: false를 반환합니다.
 * - 모든 role name은 소문자로 변환하여 비교합니다.
 * @example
 * // 기본 사용법
 * const { isAdmin, roles, scope } = useCheckAdmin();
 * if (isAdmin) {
 *   // admin 권한이 있는 경우
 * }
 *
 * @example
 * // 커스텀 admin roles 지정
 * const { isAdmin } = useCheckAdmin({
 *   adminRoles: {
 *     [TOKEN_SCOPE.DOMAIN]: ['admin', 'super-admin'],
 *     [TOKEN_SCOPE.PROJECT]: ['admin', 'project-owner'],
 *   },
 * });
 */
const useCheckAdmin = (request?: UseCheckAdminRequest) => {
  const { adminRoles = {} } = request || {};
  const {
    state: { scope },
  } = useServiceAppState();
  const result: UseCheckAdminResponse = {
    isAdmin: false,
    scope,
    roles: [],
  };
  const { data } = useUserInfoState();

  const adminRolesByScope = useMemo(() => {
    if (scope === TOKEN_SCOPE.DOMAIN) {
      return adminRoles[TOKEN_SCOPE.DOMAIN] || DomainScopeAdminRoles;
    }
    return adminRoles[TOKEN_SCOPE.PROJECT] || ProjectScopeAdminRoles;
  }, [adminRoles, scope]);

  if (!data?.roles) return result;

  const { roles } = data;

  const reDefineRoles = (
    roles.filter(role => !!role?.name) as RequiredBy<ResponseCloudRole, 'name'>[]
  ).map(role => ({
    ...role,
    name: role.name.toLowerCase(),
  }));
  const reDefinedRoleNames: string[] = reDefineRoles.map(role => role.name);

  const hasAdminRole = reDefinedRoleNames.some(roleName => adminRolesByScope.includes(roleName));

  return { ...result, isAdmin: !!hasAdminRole, roles: reDefinedRoleNames, scope };
};

export default useCheckAdmin;
